import React from 'react'
import {useAuthClient} from '../utils/use-auth-client'
import {useUserFeatures} from '../context/user-features-context'
import {FeatureList} from '../components/feature-flags/feature-list'
import {FeatureDetail} from '../components/feature-flags/feature-detail'
import {AssignmentForm} from '../components/feature-flags/assignment-form'
import {DeleteConfirmModal} from '../components/feature-flags/delete-confirm-modal'
import {
  PageWrapper,
  PageHeader,
  PageTitle,
  SearchInput,
  PanelContainer,
  PrimaryButton,
  ErrorBanner,
} from './features.styled'

function FeatureFlags() {
  const client = useAuthClient()
  const {Feature_Flags_Write_Access: hasWriteAccess} = useUserFeatures()

  const [featureFlags, setFeatureFlags] = React.useState([])
  const [isError, setIsError] = React.useState(false)
  const [selectedFeature, setSelectedFeature] = React.useState(null)
  const [search, setSearch] = React.useState('')
  const [isFormOpen, setIsFormOpen] = React.useState(false)
  const [formInitialDesc, setFormInitialDesc] = React.useState('')
  const [deleteTarget, setDeleteTarget] = React.useState(null)

  const fetchFeatureFlags = React.useCallback(async () => {
    setIsError(false)
    try {
      const data = await client('feature-flags')
      setFeatureFlags(data)
      return data
    } catch {
      setIsError(true)
    }
  }, [client])

  React.useEffect(() => {
    fetchFeatureFlags()
  }, [fetchFeatureFlags])

  const filteredFeatures = React.useMemo(() => {
    if (!search.trim()) return featureFlags
    const term = search.toLowerCase()
    return featureFlags.filter(
      f =>
        f.featureDesc.toLowerCase().includes(term) ||
        f.assignments.some(a => a.entityValue.toLowerCase().includes(term)),
    )
  }, [featureFlags, search])

  const filteredSelectedFeature = React.useMemo(() => {
    if (!selectedFeature) return null
    if (!search.trim()) return selectedFeature
    const term = search.toLowerCase()
    return {
      ...selectedFeature,
      assignments: selectedFeature.assignments.filter(a =>
        a.entityValue.toLowerCase().includes(term),
      ),
    }
  }, [selectedFeature, search])

  const handleMutationSuccess = React.useCallback(
    async newFeatureDesc => {
      setIsFormOpen(false)
      setDeleteTarget(null)
      const fresh = await fetchFeatureFlags()
      if (!fresh) return

      if (newFeatureDesc) {
        // POST: seleccionar el feature recién creado/actualizado
        const created = fresh.find(f => f.featureDesc === newFeatureDesc)
        setSelectedFeature(created || null)
      } else if (selectedFeature) {
        // DELETE: mantener selección si el feature aún existe
        const stillExists = fresh.find(
          f => f.featureDesc === selectedFeature.featureDesc,
        )
        setSelectedFeature(stillExists || null)
      }
    },
    [fetchFeatureFlags, selectedFeature],
  )

  const openNewFeatureForm = () => {
    setFormInitialDesc('')
    setIsFormOpen(true)
  }

  const openAddRuleForm = () => {
    setFormInitialDesc(selectedFeature.featureDesc)
    setIsFormOpen(true)
  }

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Feature Flags</PageTitle>
        {hasWriteAccess && (
          <PrimaryButton onClick={openNewFeatureForm}>
            + New Feature
          </PrimaryButton>
        )}
      </PageHeader>

      <SearchInput
        type="text"
        placeholder="Search by feature name or value..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {isError && (
        <ErrorBanner>
          Failed to load feature flags. Please refresh the page.
        </ErrorBanner>
      )}

      {!isError && (
        <PanelContainer>
          <FeatureList
            features={filteredFeatures}
            selectedFeature={selectedFeature}
            onSelect={setSelectedFeature}
          />
          <FeatureDetail
            feature={filteredSelectedFeature}
            onAddRule={openAddRuleForm}
            onRemoveClick={assignment =>
              setDeleteTarget({
                ...assignment,
                featureDesc: selectedFeature.featureDesc,
              })
            }
            hasWriteAccess={hasWriteAccess}
          />
        </PanelContainer>
      )}

      {isFormOpen && (
        <AssignmentForm
          initialFeatureDesc={formInitialDesc}
          isDescReadOnly={!!formInitialDesc}
          initialAssignments={selectedFeature?.assignments ?? []}
          onSuccess={handleMutationSuccess}
          onCancel={() => setIsFormOpen(false)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          assignment={deleteTarget}
          onSuccess={handleMutationSuccess}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PageWrapper>
  )
}

export {FeatureFlags}
